import React, { useState, useRef, useEffect } from 'react';

const Terminal = ({ isRoot, fs, setFs, toggleWindow }) => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([
    "DiptanshuOS [Version 1.0] (c) 2025 CyberSec Student.",
    "Type 'help' to see available commands.",
    ""
  ]);

  const [cwd, setCwd] = useState('/home/visitor');
  
  
  // Auto-scroll to bottom when history changes
  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  // Handle key press (Enter)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const raw = input.trim();
      const tokens = raw.match(/(?:"([^"]+)")|(?:'([^']+)')|([^\s]+)/g) || [];
      const commandLower = tokens[0]?.toLowerCase() ?? '';
      const args = tokens.slice(1).map(a => (a.startsWith('"') || a.startsWith("'") ? a.slice(1, -1) : a));

      // helpers
      const getPartsFromPath = (pathStr) => {
        if (!pathStr || pathStr === '.') return [];
        return pathStr.split('/').filter(Boolean);
      };

      const getNodeByParts = (parts) => {
        let node = fs;
        for (const p of parts) {
          if (!node.children || !node.children[p]) return null;
          node = node.children[p];
        }
        return node;
      };

      const resolvePath = (pathStr) => {
        if (!pathStr || pathStr === '.') return { node: getNodeByParts(cwd === '/' ? [] : cwd.split('/').filter(Boolean)), path: cwd };
        const parts = getPartsFromPath(pathStr);
        const cwdParts = cwd === '/' ? [] : cwd.split('/').filter(Boolean);
        let walk = pathStr.startsWith('/') ? [] : [...cwdParts];
        for (const part of parts) {
          if (part === '.') continue;
          if (part === '..') { walk.pop(); continue; }
          walk.push(part);
        }
        const node = getNodeByParts(walk);
        return { node, path: '/' + walk.join('/') };
      };

      const resolveParent = (pathStr) => {
        // returns { parentNode, name, parentParts }
        if (!pathStr) return { parentNode: null, name: null, parentParts: null };
        const parts = pathStr.split('/').filter(Boolean);
        const name = parts.pop();
        const cwdParts = cwd === '/' ? [] : cwd.split('/').filter(Boolean);
        const parentParts = parts.length ? parts : cwdParts;
        const parent = getNodeByParts(parentParts);
        return { parentNode: parent, name, parentParts };
      };

      const formatPermissions = (node) => {
        // simplistic: directories get 'd', files '-' and fixed perms
        const t = node.type === 'dir' ? 'd' : '-';
        return t + 'rwxr-xr-x';
      };

      const formatLong = (name, node) => {
        const perms = formatPermissions(node);
        const nlink = node.type === 'dir' ? 2 : 1;
        const owner = isRoot ? 'root' : 'visitor';
        const group = isRoot ? 'root' : 'visitor';
        const size = node.type === 'file' ? (node.content || '').length : 4096;
        const mtime = 'Nov 25 2025';
        return { 
          type: 'text', 
          content: `${perms} ${nlink} ${owner} ${group} ${size} ${mtime} ${name}`,
          isDir: node.type === 'dir'
        };
      };

      const listDirectory = (node, opts = { all: false, long: false }) => {
        if (!node || node.type !== 'dir') return '';
        const names = Object.keys(node.children || {});
        const filtered = names.filter(n => opts.all ? true : !n.startsWith('.'));
        if (opts.long) {
          return filtered.map(n => formatLong(n, node.children[n]));
        }
        // For simple listing, return a special object with array of items
        return {
          type: 'listing',
          items: filtered.map(n => ({
            name: n,
            isDir: node.children[n].type === 'dir'
          }))
        };
      };

      let response = '';

      // Output redirection support for simple cases: `echo ... > filename`
      const redirectMatch = raw.match(/^(.*?)\s*>\s*(\S+)$/);
      if (redirectMatch) {
        const left = redirectMatch[1];
        const targetPath = redirectMatch[2];
        const leftTokens = left.match(/(?:("[^"]+")|('[^']+')|([^\s]+))/g) || [];
        const leftCmd = leftTokens[0]?.toLowerCase() ?? '';
        if (leftCmd === 'echo') {
          const content = left.slice(left.indexOf(' ')+1) || '';
          // compute full path parts
          const fullParts = (targetPath.startsWith('/') ? [] : (cwd === '/' ? [] : cwd.split('/').filter(Boolean))).concat(targetPath.split('/').filter(Boolean));
          const name = fullParts.pop();
          const parentParts = fullParts;
          // immutably update fs
          setFs(prevFs => {
            const newFs = JSON.parse(JSON.stringify(prevFs));
            let node = newFs;
            for (const p of parentParts) {
              if (!node.children) node.children = {};
              if (!node.children[p]) node.children[p] = { name: p, type: 'dir', children: {} };
              node = node.children[p];
            }
            node.children[name] = { name, type: 'file', content };
            return newFs;
          });
          response = '';
        } else {
          response = 'Only echo is supported for redirection in this demo.';
        }
      } else {
        // Normal commands
        switch (commandLower) {
          case 'help':
            response = `Available Commands:

ℹ️  Information:
  about           About me
  projects        View my projects
  contact         Contact information
  help            Show this message

📁 Navigation & Files:
  ls [-al]        List directory contents
  cd [path]       Change directory
  pwd             Print working directory
  cat [file]      Display file contents
  mkdir [dir]     Create directory
  rm/rmdir        Remove files/directories (disabled)

💻 System:
  clear, cls      Clear terminal screen
  whoami          Display current user
  sudo [cmd]      Attempt privilege escalation
  echo [text]     Print text to terminal
  neofetch        Display system information

💡 Hint: Some files are shy and hide from standard lists...`;
            break;
          case 'cls':
          case 'clear':
            setHistory([]);
            setInput('');
            return;
          case 'whoami':
            response = isRoot ? 'root' : 'visitor';
            break;
          case 'sudo':
            // Check if it's "sudo su" specifically
            if (args[0] === 'su') {
              response = "User is not in the sudoers file. This incident will be reported. (Just kidding, try finding the root password!)\n\n💡 Hint: Check the bottom dock for a way to gain access.";
            } else {
              response = "User is not in the sudoers file. This incident will be reported.";
            }
            break;
          case 'about':
            if (toggleWindow) {
              toggleWindow('about', true);
              response = "Opening About Me window...";
            } else {
              response = "Diptanshu Dhawan - Cybersecurity Student & Developer.";
            }
            break;
          case 'projects':
            if (toggleWindow) {
              toggleWindow('projects', true);
              response = "Opening Projects window...";
            } else {
              response = "Check the 'projects.txt' file or use the GUI file manager.";
            }
            break;
          case 'contact':
            if (toggleWindow) {
              toggleWindow('contact', true);
              response = "Opening Contact Me window...";
            } else {
              response = "Email: diptanshu@example.com";
            }
            break;
          case 'ls': {
            // parse flags and paths
            const flags = args.filter(a => a.startsWith('-')).join('');
            const paths = args.filter(a => !a.startsWith('-'));
            const opts = { all: flags.includes('a'), long: flags.includes('l') };
            
            const targets = paths.length ? paths : ['.'];
            const outputs = [];
            for (let i = 0; i < targets.length; i++) {
              const tgt = targets[i];
              const { node } = resolvePath(tgt);
              if (!node) {
                outputs.push(`ls: cannot access '${tgt}': No such file or directory`);
                continue;
              }
              if (node.type === 'file') {
                const item = opts.long ? formatLong(node.name, node) : { type: 'text', content: node.name, isDir: false };
                outputs.push(item);
              } else {
                const listing = listDirectory(node, opts);
                if (targets.length > 1) outputs.push(`${tgt}:`);
                if (opts.long) {
                  outputs.push(...listing);
                } else {
                  outputs.push(listing);
                }
              }
            }
            response = outputs;
            break;
          }
          case 'pwd':
            response = cwd;
            break;
          case 'cd': {
            const target = args[0] || '/home/visitor';
            const { node, path } = resolvePath(target);
            if (!node) response = `cd: ${target}: No such file or directory`;
            else if (node.type === 'file') response = `cd: ${target}: Not a directory`;
            else setCwd(path === '/' ? '/' : path.replace(/\\/g, '/'));
            break;
          }
          case 'mkdir': {
            if (!args[0]) {
              response = 'mkdir: missing operand';
              break;
            }
            const target = args[0];
            const { parentNode, name } = resolveParent(target);
            if (!parentNode) {
              response = `mkdir: cannot create directory '${target}': No such file or directory`;
              break;
            }
            if (parentNode.children[name]) {
              response = `mkdir: cannot create directory '${target}': File exists`;
              break;
            }
            setFs(prevFs => {
              const newFs = JSON.parse(JSON.stringify(prevFs));
              // navigate to parent
              
              // Find current dir node in newFs
              let curr = newFs;
              const cwdParts = cwd.split('/').filter(Boolean);
              for (const p of cwdParts) {
                curr = curr.children[p];
              }
              curr.children[target] = { name: target, type: 'dir', children: {} };
              return newFs;
            });
            response = '';
            break;
          }
          case 'rm':
          case 'rmdir':
            response = "Permission denied. (File deletion disabled for demo)";
            break;
          case 'cat': {
            if (!args[0]) {
              response = 'cat: missing operand';
              break;
            }
            const target = args[0];
            const { node } = resolvePath(target);
            if (!node) response = `cat: ${target}: No such file or directory`;
            else if (node.type === 'dir') response = `cat: ${target}: Is a directory`;
            else if (node.restricted && !isRoot) response = node.content; // Restricted files show their content which is "Permission denied"
            else {
              // Special handling for specific files - open windows instead
              if (node.name === 'resume.pdf') {
                if (toggleWindow) {
                  toggleWindow('resume', true);
                  response = `📄 Opening Resume Viewer in new window...\n\n💡 Tip: The resume has been opened in a separate window for better viewing.\n    You can also click the Resume icon on the desktop or dock!`;
                } else {
                  response = node.content;
                }
              } else if (node.name === 'about.txt') {
                if (toggleWindow) {
                  toggleWindow('about', true);
                  response = `👤 Opening About Me window...\n\n💡 The About Me section contains my skills, certifications, and career timeline.`;
                } else {
                  response = node.content;
                }
              } else if (node.name === 'projects.txt') {
                if (toggleWindow) {
                  toggleWindow('projects', true);
                  response = `💻 Opening Projects window...\n\n💡 Explore my cybersecurity projects and tools!`;
                } else {
                  response = node.content;
                }
              } else {
                response = node.content;
              }
            }
            break;
          }
          case 'echo':
            response = args.join(' ');
            break;
          case 'neofetch': {
            const art = `   ████████████
 ██▀▀░░░░░░▀▀██
██░░░░░░░░░░░██
██░░░░░░░░░░░██
 ██▄▄░░░░░░▄▄██
   ████████████`;

            const info = [
              'diptanshu@portfolio',
              '─────────────────',
              'OS: Diptanshu v1.0',
              'Host: B.Tech CSE Student',
              'Kernel: Cybersecurity Enthusiast',
              'Uptime: 20 years',
              'Packages: Python, Linux, React',
              'Shell: bash • zsh • pwsh',
              'DE: Penetration Testing',
              'WM: CTF Player',
              '',
              'Email: dhawandiptanshu.work@gmail.com'
            ];

            const artLines = art.split('\n');
            const output = [];
            
            for (let i = 0; i < info.length; i++) {
              const artPart = (artLines[i] || '').padEnd(20, ' ');
              const infoPart = info[i] || '';
              output.push(artPart + infoPart);
            }
            
            response = output.join('\n');
            break;
          }
          case '':
            break;
          default:
            response = `Command not found: ${commandLower}`;
        }
      }

      setHistory(prev => [...prev, { type: 'prompt', isRoot, command: input }, ...(Array.isArray(response) ? response : [response]), ""]);
      setInput('');
    }
  };

  return (
    <div 
      className="terminal" 
      onClick={() => document.getElementById('terminal-input')?.focus()}
      style={{ 
        height: '100%', 
        background: '#0a0014', 
        color: '#00ff41', 
        fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", "Courier New", monospace', 
        padding: '10px', 
        overflowY: 'auto',
        fontSize: '14px',
        fontWeight: '500'
      }}
    >
      {history.map((line, i) => (
        <div key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: '4px' }}>
          {typeof line === 'string' ? line : (
            // Handle structured output
            line.type === 'prompt' ? (
              // Render command prompt with proper colors
              <div>
                <span style={{ color: line.isRoot ? '#ff006e' : '#ffffff', fontWeight: '700' }}>
                  {line.isRoot ? 'root@diptanshu' : 'visitor@diptanshu'}
                </span>
                <span style={{ color: '#b026ff', fontWeight: 500 }}>
                  {line.isRoot ? '#' : '$'}
                </span>
                <span style={{ color: '#00ff41' }}> {line.command}</span>
              </div>
            ) : line.type === 'listing' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                {line.items.map((item, idx) => (
                  <span key={idx} style={{ 
                    color: item.isDir ? '#4a9eff' : '#00ff41',
                    fontWeight: item.isDir ? 'bold' : 'normal'
                  }}>
                    {item.name}
                  </span>
                ))}
              </div>
            ) : line.type === 'text' ? (
              // Long listing format line
              <span style={{ color: line.isDir ? '#4a9eff' : '#00ff41' }}>{line.content}</span>
            ) : null
          )}
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center' }} ref={messagesEndRef}>
        <span style={{ marginRight: '8px' }}>
          <span style={{ color: isRoot ? '#ff006e' : '#ffffff', fontWeight: '700', whiteSpace: 'nowrap', letterSpacing: '0.3px' }}>{isRoot ? 'root@diptanshu' : 'visitor@diptanshu'}</span>
          <span style={{ color: '#b026ff', fontWeight: 500 }}>{isRoot ? '#' : '$'}</span>
        </span>
        <input
          id="terminal-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#00ff41',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            flex: 1,
            outline: 'none',
            caretColor: isRoot ? '#ff006e' : '#00ff41'
          }}
          autoFocus
          autoComplete="off"
        />
      </div>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default Terminal;
